/**
 * What an interaction log must never hold.
 *
 * Mirrors backend `interaction_log_service.redact`: secrets are matched on whole words
 * in a field's name, never as a substring. A substring match on "pin" would also blank
 * `shipping`, `mapping` and `pincode` (a postal code) — the values a debug log exists
 * to keep. Customer names and phone numbers are recorded as typed (owner decision
 * 2026-09-17). The server redacts again on arrival; this copy means a secret never
 * reaches the phone's own storage either.
 */
export const REDACTED = "***";

const WORDS = /[A-Z]?[a-z]+|[A-Z]+(?![a-z])|\d+/g;
const SENSITIVE_WORDS = new Set([
  "pin", "pins", "password", "passcode", "passwd", "secret", "token", "authorization", "otp", "cookie",
]);
const SENSITIVE_JOINED = new Set(["apikey", "pinhash", "accesstoken", "refreshtoken", "authtoken"]);
const JWT = /eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}/g;
const BEARER = /\bbearer\s+[A-Za-z0-9._~+/=-]+/gi;
const SENSITIVE_AUTOCOMPLETE = new Set(["current-password", "new-password", "one-time-code"]);

const MAX_STRING = 4000;
const MAX_ITEMS = 200;
const MAX_DEPTH = 8;

export function isSensitiveKey(key) {
  if (typeof key !== "string" || !key) return false;
  const words = (key.match(WORDS) || []).map(word => word.toLowerCase());
  return words.some(word => SENSITIVE_WORDS.has(word)) || SENSITIVE_JOINED.has(words.join(""));
}

export function redactString(value) {
  const cleaned = String(value).replace(JWT, REDACTED).replace(BEARER, `Bearer ${REDACTED}`);
  return cleaned.length > MAX_STRING ? `${cleaned.slice(0, MAX_STRING)}…` : cleaned;
}

export function redactValue(value, depth = 0) {
  if (depth > MAX_DEPTH) return "…";
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return redactString(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.slice(0, MAX_ITEMS).map(item => redactValue(item, depth + 1));
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") {
    const out = {};
    for (const [key, item] of Object.entries(value).slice(0, MAX_ITEMS)) {
      out[key] = isSensitiveKey(key) ? REDACTED : redactValue(item, depth + 1);
    }
    return out;
  }
  return String(value);
}

/** A URL with sensitive query parameters blanked — the realtime socket carries its JWT there. */
export function redactUrl(url) {
  if (!url) return url;
  const text = String(url);
  const queryAt = text.indexOf("?");
  if (queryAt === -1) return redactString(text);
  const base = text.slice(0, queryAt);
  const query = text.slice(queryAt + 1);
  const cleaned = query
    .split("&")
    .map(pair => {
      const [key, ...rest] = pair.split("=");
      let name = key;
      try { name = decodeURIComponent(key); } catch { /* keep the raw key */ }
      return isSensitiveKey(name) ? `${key}=${REDACTED}` : [key, ...rest].join("=");
    })
    .join("&");
  return redactString(`${base}?${cleaned}`);
}

/** Whether a form control holds a secret, judged from everything that names it. */
export function isSensitiveField(element) {
  if (!element || typeof element.getAttribute !== "function") return false;
  const type = (element.getAttribute("type") || "").toLowerCase();
  if (type === "password") return true;
  const autocomplete = (element.getAttribute("autocomplete") || "").toLowerCase();
  if (SENSITIVE_AUTOCOMPLETE.has(autocomplete)) return true;
  // inputmode=numeric on a short field is how PIN pads are built here, but so is a
  // quantity box — so it is NOT treated as sensitive on its own.
  return ["name", "id", "data-testid", "aria-label", "placeholder"].some(attribute =>
    isSensitiveKey(element.getAttribute(attribute) || "")
  );
}
